import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { UserService } from '../../services/uppy/user.service';
import { NavigationEnd, Router } from '@angular/router';

declare function getCurUrl(): string
@Component({
  selector: 'app-main-sidebar',
  templateUrl: './main-sidebar.component.html',
  styleUrls: ['./main-sidebar.component.scss'],
  standalone: false
})
export class MainSidebarComponent implements OnInit {
  @Input() contextMenu: boolean = false;
  @Input() site: 'public' | 'tp' | 'learner' | 'employer' | 'login' = 'public';
  currentLang: string;
  constructor(private cdr: ChangeDetectorRef,
    @Inject('UserService') public userService: UserService,
    public translateService: TranslateService,
    public router: Router) {
    this.currentLang = translateService.currentLang
    translateService.onLangChange.subscribe(lang => this.currentLang = lang.lang);
  }

  dashboardSelected = false;
  scheduleSelected = false;
  teamsSelected = false;
  learnersSelected = false;
  notificationsSelected = false;
  contactUsSelected = false;
  faqSelected = false;
  reportingSelected = false;
  accSettingsSelected = false;

  isCertificationSelected = false;
  isScheduleSelected = false;
  isNotificationsSelected = false;
  isContactUsSelected = false;
  isProfileSelected = false;
  isFaqSelected = false;

  isWorkerRecordsSelected = false;

  isSoseSelected = false;

  showDashboard = false;
  showSchedule = false;
  showTeams = false;
  showLearners = false;
  showNotifications = false;
  showReporting = false;
  showAccountSettings = false;
  tpAppUtils = TrainingProgramApplicationUtils;

  employerLinks: Map<string, string> = new Map<string, string>();
  currentlySelectedEmployerRouteName: string = '';

  ngOnInit(): void {
    if (this.router.url && this.router.url.toLowerCase().indexOf('/employer/') > -1) {
      this.setEmployerRoutes();
    }
    this.showHideBasedOnUserRoles();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.getActiveButtonFromURL();
      }
    });
  }

  ngAfterViewInit(): void {
    this.getActiveButtonFromURL();
  }

  get hasUnreadNotifications(): boolean {
    return Number(this.userService.unreadNotificationCount) > 0;
  }
  
  getActiveButtonFromURL(url = '') {
    let activeRoute = url ? url : getCurUrl().toLowerCase();
    this.dashboardSelected = activeRoute.indexOf('/training-provider/my-training-programs') > -1;
    this.scheduleSelected = activeRoute.indexOf('/training-provider/training-schedules') > -1;
    this.teamsSelected = activeRoute.indexOf('/training-provider/teams') > -1;
    this.learnersSelected = activeRoute.indexOf('/training-provider/my-learners') > -1;
    this.notificationsSelected = activeRoute.indexOf('/training-provider/my-notifications') > -1;
    this.contactUsSelected = activeRoute.indexOf('/training-provider/contact-us') > -1;
    this.contactUsSelected = activeRoute.indexOf('/training-provider/contact-us') > -1;
    this.faqSelected = activeRoute.indexOf('/training-provider/faqs') > -1;
    this.reportingSelected = activeRoute.indexOf('/training-provider/reporting') > -1;
    this.accSettingsSelected = activeRoute.indexOf('/training-provider/account-settings') > -1;

    this.isCertificationSelected = activeRoute.indexOf('/worker/certifications') > -1;
    this.isProfileSelected = activeRoute.indexOf('/worker/profile-review') > -1;
    this.isScheduleSelected = activeRoute.indexOf('/worker/training-schedules') > -1;
    this.isNotificationsSelected = activeRoute.indexOf('/worker/notifications') > -1;
    this.isContactUsSelected = activeRoute.indexOf('/worker/contact-us') > -1;
    this.isFaqSelected = activeRoute.indexOf('/worker/faq') > -1;

    this.isWorkerRecordsSelected = activeRoute.indexOf('/employer/my-worker-records') > -1;

    this.isSoseSelected = activeRoute.indexOf('/employer/sose') > -1;

    this.setCurrentlySelectedEmployerRoute(activeRoute);
    this.cdr.detectChanges()
  }

  showHideBasedOnUserRoles() {
    this.userService.GetCurrentUser().subscribe(user => {
      const userRoles = user.Roles?.map((role) => role.PersonRoleId?.toUpperCase());
      this.showDashboard = false;
      this.showSchedule = false;
      this.showTeams = false;
      this.showLearners = false;
      this.showNotifications = false;
      this.showReporting = false;
      if (userRoles?.includes(TrainingProgramApplicationUtils.DesignatedSigningAuthorityId)
        || userRoles?.includes(TrainingProgramApplicationUtils.AdministratorId)) {
        this.showDashboard = true;
        this.showSchedule = true;
        this.showLearners = true;
        this.showNotifications = true;
        this.showTeams = true;
        this.showReporting = true;
        this.showAccountSettings = true;
        if (this.showNotifications) {
          this.userService.LoadUnreadNotificationCount();
        }
      }
      else if (userRoles?.includes(TrainingProgramApplicationUtils.AlternateSigningAuthorityId)) {
        this.showDashboard = true;
        this.showSchedule = true;
        this.showLearners = true;
        this.showNotifications = true;
        this.showTeams = true;
        if (this.showNotifications) {
          this.userService.LoadUnreadNotificationCount();
        }
      }
      else if (userRoles?.includes(TrainingProgramApplicationUtils.AffiliateAdministratorId)) {
        this.showDashboard = false;
        this.showSchedule = true;
        this.showLearners = true;
        this.showNotifications = false;
        this.showTeams = false;
      }
      else if (userRoles?.includes(TrainingProgramApplicationUtils.InstructorId) || userRoles?.includes(TrainingProgramApplicationUtils.AffiliateInstructorId)) {
        this.showDashboard = false;
        this.showSchedule = true;
        this.showLearners = false;
        this.showNotifications = false;
        this.showTeams = false;
      }
    });
  }

  goTo(url) {
    this.router.navigate([url]);
    this.getActiveButtonFromURL(url);
  }

  setEmployerRoutes() {
    this.employerLinks.set('/employer/my-worker-records', 'myWorkerRecords',);
    this.employerLinks.set('/employer/my-teams', 'myTeams');
    this.employerLinks.set('/employer/notifications', 'notifications');
    this.employerLinks.set('/employer/faq', 'faq');
    this.employerLinks.set('/employer/contact-us', 'contactUs');
  }

  setCurrentlySelectedEmployerRoute(activeRoute: string) {
    this.currentlySelectedEmployerRouteName = '';
    if (this.employerLinks.size <= 0) return;
    for (let [key, value] of this.employerLinks.entries()) {
      if (activeRoute.indexOf(key) > -1)
        this.currentlySelectedEmployerRouteName = value;
    }
  }
}
